import React, {useState, useEffect, useCallback, useRef} from 'react';
import SMCTextInput from '../../../components/Simple/SMCTextInput';
import {useTranslation} from 'react-i18next';
import SMCDayPickerInput from '../../../components/Simple/SMCDayPickerInput';
import FilterLayout from '../FilterLayout';
import RowLayout from '../../../components/Layouts/RowLayout';
import SMCExpansionPanel from '../../../components/Simple/SMCExpansionPanel';
import SMCIcon from '../../../components/Simple/SMCIcon';
import {SMCSelect, SMCOption} from '../../../components/Simple/SMCSelect';
import Api from '../../../api/SMPFetchHelper';
import {SessionDataGetSchema} from '../../../api/DBSchema/SessionData';
import {CommunityFilterSchema} from '../../../api/DBSchema/Community';


function DateRangeChooser({
    fromDate,
    toDate,
    setFromDate,
    setToDate
}) {
    return (
        <>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>From:</span>
            <SMCDayPickerInput
                placeholder="From..."
                value={fromDate}
                onChange={setFromDate}
                maxDate={toDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setFromDate(null)} />
        </RowLayout>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>To:</span>
            <SMCDayPickerInput
                placeholder="To..."
                value={toDate}
                onChange={setToDate}
                minDate={fromDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setToDate(null)} />
        </RowLayout>
        </>
    );
}

function CommunityFilterPane({
    initialFilter,
    onFilterChange,
    open
}) {
    const { t } = useTranslation();
    const didMountRef = useRef(true);
    const freeTextSearchRef = useRef();

    useEffect(() => {
        if (open && freeTextSearchRef.current) {
            freeTextSearchRef.current.focus();
        }
    }, [open]);

    const [availableCommunities, setAvailableCommunities] = useState(null);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [/*pageError*/, setPageError] = useState(false);

    const [memberOf, setMemberOf] = useState('');

    const [freeTextSearch, setFreeTextSearch] = useState(
        initialFilter.freeTextSearch ? initialFilter.freeTextSearch.value : ''
    );
        
    const [creationFromDate, setCreationFromDate] = useState(null);
    const [creationToDate, setCreationToDate] = useState(null);

    const [modificationFromDate, setModificationFromDate] = useState(null);
    const [modificationToDate, setModificationToDate] = useState(null);
    

    const resetToDefaults = useCallback(() => {
        setFreeTextSearch('');
        
        setMemberOf('');

        setCreationFromDate(null);
        setCreationToDate(null);
        
        setModificationFromDate(null);
        setModificationToDate(null);
    }, []);


    // Update filter request object on value change
    useEffect(() => {
        // Don't run on first render
        if (didMountRef.current === true) {
            didMountRef.current = false;
            return;
        }

        const filterRequestObj = {};
        // Community has no free text search filter, search on name
        if (freeTextSearch) {
            filterRequestObj[CommunityFilterSchema.name] = { value: '*'+freeTextSearch+'*' };
        }
        
        // Member of
        if (memberOf) {
            filterRequestObj[CommunityFilterSchema.memberOf] = { value: memberOf };
        }

        // Creation date range
        if (creationFromDate || creationToDate) {
            filterRequestObj[CommunityFilterSchema.creationDate] = {
                value: {
                    from: creationFromDate ? creationFromDate.getTime() : null,
                    to: creationToDate ? creationToDate.getTime() : null
                }
            };
        }
        // Modification date range
        if (modificationFromDate || modificationToDate) {
            filterRequestObj[CommunityFilterSchema.modificationDate] = {
                value: {
                    from: modificationFromDate ? modificationFromDate.getTime() : null,
                    to: modificationToDate ? modificationToDate.getTime() : null
                }
            };
        }
        
        onFilterChange(filterRequestObj);
    }, [
        freeTextSearch, onFilterChange, memberOf,
        creationFromDate, creationToDate,
        modificationFromDate, modificationToDate
    ]);

    useEffect(() => {
        (async () => {
            try {
                const sessionDataResp = await Api.getSessionData({
                    attributes: [SessionDataGetSchema.availableCommunities]
                });
                setAvailableCommunities(sessionDataResp.data.availableCommunities);
            } catch (e) {
                console.error('CommunityFilterPane: error reading availableCommunities', e);
                setPageError(true);
            }
            setPageLoaded(true);
        })();
    }, [t]);


    return (
        <FilterLayout clearAllAction={resetToDefaults} loading={!pageLoaded}>
            <SMCTextInput
                width="auto"
                height={26}
                placeholder="Search"
                value={freeTextSearch}
                innerRef={freeTextSearchRef}
                onChange={e => setFreeTextSearch(e.target.value)}
            />
            {/* Member of tab */}
            <SMCExpansionPanel title={t('SMC_COMMUNITIES_MEMBER_OF')}>
                <RowLayout spacing={5}>
                    <SMCSelect
                        value={memberOf}
                        onChange={e => setMemberOf(e.target.value)}
                        inline
                        width="150px"
                    >
                        <SMCOption value="" />
                        {
                            availableCommunities && availableCommunities.map(commName => (
                                <SMCOption key={commName} value={commName}>{commName}</SMCOption>
                            ))
                        }
                        <SMCOption value="NONE">{t('SMC_GEN_NONE')}</SMCOption>
                    </SMCSelect>
                    <SMCIcon icon="clear" onClick={() => setMemberOf('')} />
                </RowLayout>
            </SMCExpansionPanel>
            {/* Creation tab */}
            <SMCExpansionPanel title={t('SMC_OBJECT_CREATETIME')}>
                <DateRangeChooser
                    fromDate={creationFromDate}
                    toDate={creationToDate}
                    setFromDate={setCreationFromDate}
                    setToDate={setCreationToDate}
                />
            </SMCExpansionPanel>
            {/* Modification tab */}
            <SMCExpansionPanel title={t('SMC_OBJECT_MODIFYTIME')}>
                <DateRangeChooser
                    fromDate={modificationFromDate}
                    toDate={modificationToDate}
                    setFromDate={setModificationFromDate}
                    setToDate={setModificationToDate}
                />
            </SMCExpansionPanel>
        </FilterLayout>
    );
}

export default React.memo(CommunityFilterPane);